import noop from 'utils/noop';
import {
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { isDefined } from 'utils/tsHelpers';
import { useQueryErrorStateContext } from 'modules/queryBuilder/contexts';
import { CompleteQuery, EditableQuery } from 'modules/queryBuilder/types';
import { TABLE_GQL_SORT_DIRECTION_ENUM_MAP } from 'constants/sortDirectionsEnum';
import { useListDetailsContext } from '../contexts/listDetails';
import { SegmentUsersQueryParams, SegmentUsersQueryResponse } from '../types';
import { useLazySegmentationQuery, useListId } from '../hooks';
import { DEFAULT_SEGMENTATION_QUERY, DEFAULT_PAGE, PAGE_VARIATIONS } from '../constants';
import { useUsersTableContext } from './usersTable';
import { useSegmentationQuerySchema } from '../components/queryBuilderSection/schema/hooks';
import { useToastNotification } from 'utils/hooks/useToastNotification';

export type SegmentationQueryState = {
  didError: boolean;
  didLoad: boolean;
  editableQuery: EditableQuery;
  isLoading: boolean;
  isValidQuery: boolean;
  refreshQueryResults: (params?: Partial<SegmentUsersQueryParams>) => void;
  searchQuery: CompleteQuery;
  setEditableQuery: Dispatch<SetStateAction<EditableQuery>>;
  setIsValidQuery: (valid: boolean) => void;
  setSearchQuery: (query: CompleteQuery) => void;
} & Partial<SegmentUsersQueryResponse>;

export const defaultSegmentationQueryState = {
  count: 0,
  didError: false,
  didLoad: false,
  editableQuery: DEFAULT_SEGMENTATION_QUERY as unknown as EditableQuery,
  isLoading: false,
  isValidQuery: false,
  refreshQueryResults: noop,
  results: [],
  searchQuery: DEFAULT_SEGMENTATION_QUERY,
  setEditableQuery: noop,
  setIsValidQuery: noop,
  setSearchQuery: noop,
};

const SegmentationQueryContext = createContext<SegmentationQueryState>(
  defaultSegmentationQueryState,
);
export const useSegmentationQueryContext = () => useContext(SegmentationQueryContext);

export const SegmentationQueryContextProvider: FC<PropsWithChildren<unknown>> = ({ children }) => {
  const { pageSize, sortBy: orderBy, sortDirection } = useUsersTableContext();
  const { pathWarnings } = useQueryErrorStateContext();
  const [searchQuery, setSearchQuery] = useState<CompleteQuery>(DEFAULT_SEGMENTATION_QUERY);
  const [editableQuery, setEditableQuery] = useState<EditableQuery>(
    DEFAULT_SEGMENTATION_QUERY as unknown as EditableQuery,
  );
  const [isValidQuery, setIsValidQuery] = useState<boolean>(true);
  const emailListId = useListId();
  const segmentationQuerySchema = useSegmentationQuerySchema();

  const {
    pageVariation,
    isLoading: listDetailsLoading,
    searchQuery: listDetailsSearchQuery,
  } = useListDetailsContext();
  const [initQueryDone, setInitQueryDone] = useState<boolean>(false);

  const {
    count,
    debug,
    isError: didError,
    isLoading,
    isSuccess: didLoad,
    results = [],
    errorMessage,
    segmentUsersQuery,
  } = useLazySegmentationQuery();

  /** calls error toast for  */
  useToastNotification({
    enabled: true,
    negative: didError && isDefined(errorMessage),
    negativeMessage: errorMessage,
  });

  const queryParams = useMemo(
    () => ({
      emailListId,
      orderBy,
      page: DEFAULT_PAGE,
      pageSize,
      searchQuery,
      sorting: TABLE_GQL_SORT_DIRECTION_ENUM_MAP[sortDirection],
    }),
    [pageSize, orderBy, sortDirection, searchQuery, emailListId],
  );

  /**
   * use to call segment users query with most recent query results
   */
  const refreshQueryResults = (params?: Partial<SegmentUsersQueryParams>) => {
    if (isDefined(params?.searchQuery)) {
      setSearchQuery(params.searchQuery);
    }
    segmentUsersQuery({ ...queryParams, ...params });
  };

  // set initial query done to true once list details is finished loading
  useEffect(() => {
    if (!listDetailsLoading && !initQueryDone) setInitQueryDone(true);
  }, [initQueryDone, listDetailsLoading]);

  // after initial query is true refresh results/validate
  useEffect(() => {
    if (initQueryDone && pageVariation === PAGE_VARIATIONS.STATIC) {
      refreshQueryResults(); // static lists depend on the listId from the querystring
    } else if (
      initQueryDone &&
      pageVariation === PAGE_VARIATIONS.DYNAMIC &&
      listDetailsSearchQuery
    ) {
      refreshQueryResults({ searchQuery: listDetailsSearchQuery }); // dynamic lists depend on the searchQuery
      segmentationQuerySchema.safeParse(listDetailsSearchQuery);
    }
  }, [initQueryDone, pageVariation, listDetailsSearchQuery]);

  /** Sets pathWarnings on initial load */
  useEffect(() => {
    if (pathWarnings.length > 0 && isValidQuery) {
      setIsValidQuery(false);
    }
  }, [pathWarnings, isValidQuery]);

  const value = useMemo(
    () => ({
      count,
      debug,
      didError,
      didLoad,
      editableQuery,
      isLoading,
      isValidQuery,
      refreshQueryResults,
      results,
      searchQuery,
      setEditableQuery,
      setIsValidQuery,
      setSearchQuery,
    }),
    [count, didError, didLoad, editableQuery, isLoading, isValidQuery, results, searchQuery],
  );
  return (
    <SegmentationQueryContext.Provider value={value}>{children}</SegmentationQueryContext.Provider>
  );
};
